import {
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  RefreshCw,
  XCircle,
} from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getCurrentTenantUser } from '../api';
import { StatusChip } from '../components/Avatar';
import { SectionCard } from '../components/SectionCard';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useSession } from '../contexts/SessionContext';
import {
  assessProjectReadiness,
  type ProjectReadinessStatus,
} from '../utils/project-readiness';

type PingState =
  | { status: 'checking' }
  | { status: 'ok'; latencyMs: number; at: string }
  | { status: 'failed'; message: string; at: string };

function errorMessage(error: unknown) {
  if (error instanceof Error) return error.message;
  return 'Request failed';
}

function checkIcon(status: ProjectReadinessStatus) {
  if (status === 'healthy') return <CheckCircle2 size={17} />;
  if (status === 'blocked') return <XCircle size={17} />;
  return <AlertTriangle size={17} />;
}

function DiagnosticsPage() {
  const { session } = useSession();
  const {
    workspace,
    channels,
    isEnvironmentVisible,
    selectNav,
  } = useWorkspace();

  const visibleRuntimeConfigs = workspace.runtimeConfigs.filter((config) =>
    isEnvironmentVisible(config.environment)
  );
  const visiblePromotions = workspace.promotions.filter((promotion) =>
    isEnvironmentVisible(promotion.environment)
  );
  const readiness = useMemo(
    () => assessProjectReadiness({
      releaseApps: workspace.releaseApps,
      releaseAppVersions: workspace.releaseAppVersions,
      artifacts: workspace.artifacts,
      channels,
      promotions: visiblePromotions,
      runtimeConfigs: visibleRuntimeConfigs,
    }),
    [channels, visiblePromotions, visibleRuntimeConfigs, workspace.artifacts, workspace.releaseApps, workspace.releaseAppVersions]
  );

  const [ping, setPing] = useState<PingState>({ status: 'checking' });

  const runPing = useCallback(async () => {
    if (!session) {
      setPing({ status: 'failed', message: 'No active session', at: new Date().toISOString() });
      return;
    }
    setPing({ status: 'checking' });
    const start = performance.now();
    try {
      await getCurrentTenantUser(session.accessToken);
      setPing({
        status: 'ok',
        latencyMs: Math.round(performance.now() - start),
        at: new Date().toISOString()
      });
    } catch (error: unknown) {
      setPing({
        status: 'failed',
        message: errorMessage(error),
        at: new Date().toISOString()
      });
    }
  }, [session]);

  useEffect(() => { void runPing(); }, [runPing]);

  const apiStatus: ProjectReadinessStatus = ping.status === 'ok'
    ? 'healthy'
    : ping.status === 'failed'
      ? 'blocked'
      : 'warning';
  const blocked = readiness.blocked + Number(apiStatus === 'blocked');
  const warnings = readiness.warnings + Number(apiStatus === 'warning');

  return (
    <div className="healthReport">
      <section className="healthSummaryStrip" aria-label="Project health summary">
        <div className={blocked > 0 ? 'is-blocked' : 'is-healthy'}>
          <span>Blocking</span>
          <strong>{blocked}</strong>
          <small>stop a release or recovery</small>
        </div>
        <div className={warnings > 0 ? 'is-warning' : 'is-healthy'}>
          <span>Review</span>
          <strong>{warnings}</strong>
          <small>need a person to check</small>
        </div>
        <div>
          <span>Serving</span>
          <strong>{readiness.servingTargets} / {readiness.totalTargets}</strong>
          <small>targets with a bundle</small>
        </div>
        <div>
          <span>Recoverable</span>
          <strong>{readiness.recoverableTargets} / {readiness.servingTargets}</strong>
          <small>serving targets with history</small>
        </div>
      </section>

      <SectionCard
        className="healthChecksPanel"
        label="Live report"
        title="Delivery readiness"
        description="Checks the API connection and the setup needed to deploy or restore this project."
        action={
          <button
            type="button"
            className="panelControl"
            onClick={() => void runPing()}
            disabled={ping.status === 'checking'}
          >
            <RefreshCw size={14} />
            <span>{ping.status === 'checking' ? 'Checking…' : 'Re-run API check'}</span>
          </button>
        }
      >
        <div className="healthCheckList">
          <div className={`healthCheckRow is-${apiStatus}`}>
            <span className="healthCheckIcon">{checkIcon(apiStatus)}</span>
            <span className="healthCheckCopy">
              <strong>Control plane API</strong>
              <small>
                {ping.status === 'ok'
                  ? `Authenticated round-trip completed in ${ping.latencyMs} ms.`
                  : ping.status === 'failed'
                    ? ping.message
                    : 'Checking gateway and authentication…'}
              </small>
            </span>
            <StatusChip tone={apiStatus === 'blocked' ? 'danger' : apiStatus === 'healthy' ? 'success' : 'warn'}>
              {apiStatus === 'blocked' ? 'Blocked' : apiStatus === 'healthy' ? 'Healthy' : 'Checking'}
            </StatusChip>
          </div>

          {readiness.checks.map((check) => (
            <button
              key={check.id}
              type="button"
              className={`healthCheckRow is-${check.status}`}
              onClick={() => selectNav(check.page)}
            >
              <span className="healthCheckIcon">{checkIcon(check.status)}</span>
              <span className="healthCheckCopy">
                <strong>{check.label}</strong>
                <small>{check.detail}</small>
              </span>
              <StatusChip tone={check.status === 'blocked' ? 'danger' : check.status === 'healthy' ? 'success' : 'warn'}>
                {check.status === 'blocked' ? 'Blocked' : check.status === 'healthy' ? 'Healthy' : 'Review'}
              </StatusChip>
              <span className="healthCheckAction">{check.action}<ChevronRight size={14} /></span>
            </button>
          ))}
        </div>
      </SectionCard>
    </div>
  );
}

export { DiagnosticsPage };
